import { FC } from 'react';
import { useSelector } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import { PdpRespSidebarBlurbShipping } from './PdpRespSidebarBlurbShipping';
import { PdpRespSidebarBlurbReturnPolicy } from './PdpRespSidebarBlurbReturnPolicy';
import { useRenderBlurbs } from './hooks/useRenderBlurbs';
import { RelayVariables, Store } from '../../global/relayVariableTypes';

import { PdpRespSidebarBlurbs_viewer$data } from './__generated__/PdpRespSidebarBlurbs_viewer.graphql';
import { PdpRespSidebarBlurbs_item$data } from './__generated__/PdpRespSidebarBlurbs_item.graphql';
import { PdpRespSidebarBlurbs_user$data } from './__generated__/PdpRespSidebarBlurbs_user.graphql';
import { PdpRespSidebarBlurbs_displayInfo$data } from './__generated__/PdpRespSidebarBlurbs_displayInfo.graphql';
import { PdpRespSidebarBlurbs_sku$data } from './__generated__/PdpRespSidebarBlurbs_sku.graphql';

// styles
import styles from './PdpRespSidebarBlurbs.scss';

type Props = {
    viewer: PdpRespSidebarBlurbs_viewer$data;
    item: PdpRespSidebarBlurbs_item$data;
    user: PdpRespSidebarBlurbs_user$data | null;
    displayInfo: PdpRespSidebarBlurbs_displayInfo$data | null;
    sku: PdpRespSidebarBlurbs_sku$data | null;
    currency?: string;
    itemQuantity?: number;
};

export const PdpRespSidebarBlurbsComponent: FC<Props> = ({
    currency = 'USD',
    viewer,
    item,
    user,
    displayInfo = null,
    sku,
    itemQuantity,
}) => {
    const renderBlurbs = useRenderBlurbs({ item, displayInfo });
    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );

    if (!renderBlurbs) {
        return null;
    }

    return (
        <div className={styles.container}>
            <PdpRespSidebarBlurbShipping
                currency={currency}
                item={item}
                viewer={viewer}
                user={user}
                renderTooltip
                sku={relayVariables.isMultiSku ? sku : null}
                isClient={relayVariables.isClient}
                itemQuantity={itemQuantity}
            />
            <div className={styles.returnPolicyContainer}>
                <PdpRespSidebarBlurbReturnPolicy item={item} />
            </div>
        </div>
    );
};

export const PdpRespSidebarBlurbs = createFragmentContainer(PdpRespSidebarBlurbsComponent, {
    viewer: graphql`
        fragment PdpRespSidebarBlurbs_viewer on Viewer {
            ...PdpRespSidebarBlurbShipping_viewer
        }
    `,
    item: graphql`
        fragment PdpRespSidebarBlurbs_item on Item {
            ...useRenderBlurbs_item
            ...PdpRespSidebarBlurbShipping_item
            ...PdpRespSidebarBlurbReturnPolicy_item
        }
    `,
    user: graphql`
        fragment PdpRespSidebarBlurbs_user on User {
            serviceId
            ...PdpRespSidebarBlurbShipping_user
            ...PdpRespSidebarBlurbShipping_user
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarBlurbs_displayInfo on ItemDisplayInfo {
            ...useRenderBlurbs_displayInfo
        }
    `,
    sku: graphql`
        fragment PdpRespSidebarBlurbs_sku on ItemSkuType {
            ...PdpRespSidebarBlurbShipping_sku @include(if: $isMultiSku)
        }
    `,
});
